import { Grid, Typography, Box, Button, Stack, Collapse } from '@mui/material';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { AddBox, IndeterminateCheckBox } from '@mui/icons-material';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { useGlobals } from 'src/shared/contexts/appContext';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { MemberAdvancedSearchProfiles } from './memberAdvancedSearchProfiles';
import { MemberAdvancedSearchStatuses } from './memberAdvancedSearchStatuses';
import { MemberAdvancedSearchCounty } from './memberAdvancedSearchCounty';
import { MemberAdvancedSearchDateRange } from './memberAdvancedSearchDateRange';
import { MemberAdvancedSearchExclusiveToggleButton } from './memberAdvancedSearchExclusiveToggleButton';
import { MemberAdvancedSearchMembership } from './memberAdvancedSearchMembership';
import { MemberAdvancedSearchResCause } from './memberAdvancedSearchResCause';
import { MemberAdvancedSearchGender } from './memberAdvancedSearchGender';
import { MemberAdvancedSearchExport } from './memberAdvancedSearchExport';
import { ControlledMultiDistrictPicker } from '../../../shared/components/form/controlledMultiDistrictPicker';
import { MemberAdvancedSearchTitle } from './memberAdvancedSearchTitle';
import { SaveAdvancedSearchSelection } from './saveAdvancedSearchSelection';
import { SelectionTemplatePicker } from './selectionTemplatePicker';
import { MemberExportFilter } from '../pages/memberAdvancedSearchPage';
import { MemberAdvancedSearchBoxWithTitle } from './memberAdvancedSearchBoxWithTitle';
import { MemberAdvancedSearchFormSummary } from './memberAdvancedSearchFormSummary';
import { MemberSearchFormState } from '../models/memberSearchFormState';
import { UpdateSavedSelection } from './updateSavedSelection';

type MemberAdvancedSearchProps = {
  otherContactMode: boolean;
  updateState: (state: MemberSearchFormState) => void;
  resetCallback: () => void;
  exportCallback: (format: string, filter: MemberExportFilter) => void;
};

export const MemberAdvancedSearch = (props: MemberAdvancedSearchProps) => {
  const { otherContactMode, updateState, resetCallback, exportCallback } = props;
  const { currentUser } = useGlobals();
  const [formExpanded, setFormExpanded] = useLocalStorage<boolean>('searchFormExpanded', true);
  const [openSaveSelectionModal, setOpenSaveSelectionModal] = useState<boolean>(false);
  const [openUpdateSelectionModal, setOpenUpdateSelectionModal] = useState<boolean>(false);
  const [updateSelectionOptions, setUpdateSelectionOptions] = useState<boolean>(false);
  const [currentSelection, setCurrentSelection] = useState<MemberSearchFormState | null>(null);
  const { t } = useTranslation(['common', 'member']);
  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.pageHeader'));

  const resetAll = () => {
    resetCallback();
  };

  return (
    <>
      <Box
        sx={{
          pt: 2,
          pl: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
        }}
        className='section-inset'
      >
        <Box>
          <Button onClick={() => setFormExpanded(!formExpanded)} sx={{ mb: 2, justifyContent: 'left', mr: 2, display: 'flex' }}>
            <Typography variant='body1' sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {!formExpanded && <AddBox fontSize='small' />}
              {formExpanded && <IndeterminateCheckBox fontSize='small' />}
              {formExpanded && <span>{t('member:memberAdvancedSearch.hideForm')}</span>}
              {!formExpanded && <span>{t('member:memberAdvancedSearch.showForm')}</span>}
            </Typography>
          </Button>
        </Box>
        <MemberAdvancedSearchFormSummary resetAll={resetAll} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'center' },
            marginLeft: { xs: 0, sm: 3 },
            marginTop: { xs: 1, sm: 0 },
            marginRight: { sm: 3 },
          }}
        >
          <SelectionTemplatePicker
            updateSelectionOptions={updateSelectionOptions}
            updateState={updateState}
            setUpdateSelectionOptions={setUpdateSelectionOptions}
            setCurrentSelection={setCurrentSelection}
            resetAll={resetAll}
          />
        </Box>
      </Box>
      <Collapse in={formExpanded}>
        <Box
          sx={{
            p: 2,
            '& .MuiTabs-root': { minHeight: 'auto', '& button': { minHeight: '30px' } },
            '& .Mui-selected ': { backgroundColor: '#dee6ed', borderRadius: 1 },
            '& .MuiTabs-indicator': { display: 'none' },
            '& input': {
              fontWeight: `600 !important`,
            },
            '& input::placeholder': {
              fontWeight: `100 !important`,
            },
            '& .MuiInputBase-root': {
              backgroundColor: '#fff',
            },
          }}
        >
          <Grid container spacing={2}>
            <MemberAdvancedSearchBoxWithTitle title={otherContactMode ? otherLabel : t('member:memberAdvancedSearch.subTitle.membership')}>
              <Grid item xs={12}>
                <MemberAdvancedSearchStatuses otherContactMode={otherContactMode} />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchMembership />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchDateRange
                  dateStartLabel={t('member:memberAdvancedSearch.admDateStart')}
                  dateStartPropName='admDateStart'
                  dateEndLabel={t('member:memberAdvancedSearch.admDateEnd')}
                  dateEndPropName='admDateEnd'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchDateRange
                  dateStartLabel={t('member:memberAdvancedSearch.dueDateStart')}
                  dateStartPropName='dueDateStart'
                  dateEndLabel={t('member:memberAdvancedSearch.dueDateEnd')}
                  dateEndPropName='dueDateEnd'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchDateRange
                  dateStartLabel={t('member:memberAdvancedSearch.payDateStart')}
                  dateStartPropName='payDateStart'
                  dateEndLabel={t('member:memberAdvancedSearch.payDateEnd')}
                  dateEndPropName='payDateEnd'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchDateRange
                  dateStartLabel={t('member:memberAdvancedSearch.resDateStart')}
                  dateStartPropName='resDateStart'
                  dateEndLabel={t('member:memberAdvancedSearch.resDateEnd')}
                  dateEndPropName='resDateEnd'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchResCause />
              </Grid>
            </MemberAdvancedSearchBoxWithTitle>

            <MemberAdvancedSearchBoxWithTitle title={t('member:memberAdvancedSearch.subTitle.contact')}>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchCounty />
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs>
                    <ControlledTextField name='postCode' label={t('member:memberAdvancedSearch.postCode')} />
                  </Grid>
                  <Grid item xs>
                    <MemberAdvancedSearchGender />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchExclusiveToggleButton
                  nameProp='hasMobilePhone'
                  label={t('member:memberAdvancedSearch.hasMobilePhone.title')}
                  truthyLabel={t('member:memberAdvancedSearch.hasMobilePhone.truthy')}
                  falsyLabel={t('member:memberAdvancedSearch.hasMobilePhone.false')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchExclusiveToggleButton
                  nameProp='hasEmailAddress'
                  label={t('member:memberAdvancedSearch.hasEmailAddress.title')}
                  truthyLabel={t('member:memberAdvancedSearch.hasEmailAddress.truthy')}
                  falsyLabel={t('member:memberAdvancedSearch.hasEmailAddress.false')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchDateRange
                  dateStartLabel={t('member:memberAdvancedSearch.birthDateStart')}
                  dateStartPropName='birthDateStart'
                  dateEndLabel={t('member:memberAdvancedSearch.birthDateEnd')}
                  dateEndPropName='birthDateEnd'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ControlledMultiDistrictPicker name='districtIds' />
              </Grid>
            </MemberAdvancedSearchBoxWithTitle>

            <MemberAdvancedSearchBoxWithTitle title={t('member:memberAdvancedSearch.subTitle.other')}>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchProfiles label={t('member:memberAdvancedSearch.profileItemIds')} propName='profileItemIds' />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchProfiles label={t('member:memberAdvancedSearch.excludedProfileItemIds')} propName='excludedProfileItemIds' />
              </Grid>
              <Grid item xs={12} md={6}>
                <MemberAdvancedSearchExclusiveToggleButton
                  nameProp='hasNewspaper'
                  label={t('member:memberAdvancedSearch.hasNewspaper.title')}
                  truthyLabel={t('member:memberAdvancedSearch.hasNewspaper.truthy')}
                  falsyLabel={t('member:memberAdvancedSearch.hasNewspaper.false')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ControlledTextField name='searchString' label={t('member:memberAdvancedSearch.searchString')} />
              </Grid>
            </MemberAdvancedSearchBoxWithTitle>
            <MemberAdvancedSearchBoxWithTitle title={t('member:memberAdvancedSearch.titleIds')}>
              <Grid item xs={12}>
                <MemberAdvancedSearchTitle />
              </Grid>
            </MemberAdvancedSearchBoxWithTitle>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <MemberAdvancedSearchExport
                  otherContactMode={otherContactMode}
                  exportCallback={(exFormat, exFilter) => exportCallback(exFormat, exFilter)}
                />
                <Stack spacing={2} direction='row'>
                  <Button variant='outlined' disableElevation size='large' onClick={() => setOpenSaveSelectionModal(true)}>
                    {t('member:memberAdvancedSearch.saveSelection')}
                  </Button>
                  {currentSelection && (
                    <Button variant='outlined' disableElevation size='large' onClick={() => setOpenUpdateSelectionModal(true)}>
                      {t('member:memberAdvancedSearch.updateSelection')}
                    </Button>
                  )}
                  <Button variant='outlined' disableElevation size='large' onClick={() => resetAll()}>
                    {t('common:button.reset')}
                  </Button>
                  <Button type='submit' variant='contained' disableElevation size='large'>
                    {t('common:button.search')}
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
      <SaveAdvancedSearchSelection
        modalOpen={openSaveSelectionModal}
        setModalOpen={setOpenSaveSelectionModal}
        setUpdateSelectionOptions={setUpdateSelectionOptions}
      />
      {currentSelection && (
        <UpdateSavedSelection
          key={currentSelection?.selectionId}
          modalOpen={openUpdateSelectionModal}
          setModalOpen={setOpenUpdateSelectionModal}
          setUpdateSelectionOptions={setUpdateSelectionOptions}
          currentSelection={currentSelection}
        />
      )}
    </>
  );
};
